
import { computed, defineComponent, PropType, reactive } from 'vue'

// components
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// realisation
import { proposal } from '@/core/realisations/proposal.realisation'

// types
import { ProposalT } from '@/core/types/proposal.types'

// composable
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: { ButtonDropdown, TheButton },
    props: {
        item: {
            type: Object as PropType<ProposalT>,
            required: true,
        },
    },
    emits: ['update', 'closed', 'deleted'],
    setup(props, { emit }) {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_delete_loading: false,
        })

        const closeProposal = async () => {
            const answer = confirm(
                `Вы действительно хотите закрыть заявку клиента ${props.item.name}?`
            )
            if (!answer) return

            try {
                state.is_loading = true

                await proposal.closeProposal(props.item.id)
                notifications.notify({
                    title: 'Заявка закрыта',
                    description: `Заявка клиента ${props.item.name} закрыта`,
                    type: 'success',
                })

                emit('closed', props.item.id)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ title: 'При закрытии заявки произошла ошибка', error })
            } finally {
                state.is_loading = false
            }
        }

        const deleteProposal = async () => {
            const answer = confirm(
                `Вы действительно хотите удалить заявку клиента ${props.item.name}?`
            )
            if (!answer) return

            try {
                state.is_delete_loading = true

                await proposal.deleteProposal(props.item.id)
                notifications.notify({
                    title: 'Заявка удалена',
                    description: `Заявка клиента ${props.item.name} удалена`,
                    type: 'success',
                })

                emit('deleted', props.item.id)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ title: 'При удалении заявки произошла ошибка', error })
            } finally {
                state.is_delete_loading = false
            }
        }

        const emitUpdate = () => {
            if (state.is_loading || state.is_delete_loading) {
                return
            }

            emit('update', { ...props.item })
        }

        const isCanClose = computed(() => {
            if (props.item.processed_at) return false
            if (!havePermissions(['cabinet-proposal-processed', 'all-root-permission']))
                return false

            return true
        })

        const isCanUpdate = computed(() => {
            if (props.item.processed_at) return false
            if (!havePermissions(['cabinet-proposal-update', 'all-root-permission'])) return false

            return true
        })

        const isCanDelete = computed(() => {
            if (!havePermissions(['cabinet-proposal-destroy', 'all-root-permission'])) return false
            return true
        })

        const isActionsShowed = computed(() => {
            if (isCanClose.value || isCanUpdate.value || isCanDelete.value) {
                return true
            }

            return false
        })

        return {
            state,
            closeProposal,
            deleteProposal,
            emitUpdate,
            isCanClose,
            isCanUpdate,
            isCanDelete,
            isActionsShowed,
        }
    },
})
